import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import {
    View,
    RefreshControl,
    Text,
    ScrollView,
    Image,
    TouchableOpacity,
    Modal,
    Alert,
} from 'react-native'
import { FlashList } from '@shopify/flash-list'
import CategoryItem from './CategoryItem'
import { useFetchCourses } from './hooks/useFetchCourses'
import LoadingOverlay from './LoadingOverlay'
import styles from './learningHomeStyles'
import { LEARNING_STATUS } from './constants'
import { subscribeProgressUpdate, getProgressVersion } from './progressBus'
import Constants from 'expo-constants'
import { useBreakpoint } from '@/redux/zustand/useDimensionStore'
import theme from '../../../theme'
import { FullScreenLoader, Loader } from '../customUtils/Components/Loader'
import { BottomSheetAlert } from '../modals/BottomSheetAlert'
const LearningHome = () => {
    const {
        data,
        isLoading,
        isFetching,
        reload,
        fetch_data,
        setIsLoading,
        setReloadFetchCourses,
    } = useFetchCourses()
    const [refresing, setRefresing] = useState(false)
    // Normalizar: si data es array (local reconstruida) usarla; si es objeto remoto, usar single.categories
    const categories = useMemo(() => {
        if (Array.isArray(data)) return data
        return data?.single?.categories
    }, [data])
    const expoConfig = Constants.expoConfig || { slug: '' }
    const breakPoint = useBreakpoint()
    const isMobile = breakPoint === 'sm'

    const onRefresh = async () => {
        setRefresing(true)

        await fetch_data().then((data) => {
            setRefresing(false)
            setIsLoading(false)
        })
    }

    // Auto-fetch al montar si no hay categorías locales disponibles
    useEffect(() => {
        if (!categories || categories.length === 0) {
            fetch_data().catch(() => {})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Evitar duplicar refetch: el hook ya escucha el focus del navegador/navegación
    // Si necesitamos forzar manualmente, usar progressBus o pull-to-refresh

    // Suscribirse a eventos de progreso y forzar refetch inmediato
    const lastProgressVersionRef = useRef(getProgressVersion())
    useEffect(() => {
        const unsubscribe = subscribeProgressUpdate(async (version) => {
            try {
                if (
                    typeof version === 'number' &&
                    version !== lastProgressVersionRef.current
                ) {
                    lastProgressVersionRef.current = version
                    // Invalidar versión local para forzar refetch fresco desde la API
                    try {
                        const { openDB } = await import('../../db/indexDB')
                        const { updateGlobalVersion } =
                            await import('../../db/indexDB')
                        const { LEARNING_STORE_NAMES } =
                            await import('../../db/constants')
                        const db = await openDB()
                        await updateGlobalVersion(
                            db,
                            LEARNING_STORE_NAMES.LEARNING_VERSION,
                            null,
                        )
                    } catch (e) {
                        console.warn(
                            '[LearningHome] Error invalidating local version on progress update:',
                            e,
                        )
                    }
                    setReloadFetchCourses()
                }
            } catch {}
        })
        return () => {
            try {
                unsubscribe && unsubscribe()
            } catch {}
        }
    }, [setReloadFetchCourses])

    // Diagnóstico: conteo de categorías y cursos
    const { catCount, courseCount } = useMemo(() => {
        const cats = categories || []
        const courses = cats.reduce(
            (acc, c) => acc + (c?.repository?.length || 0),
            0,
        )
        return { catCount: cats.length, courseCount: courses }
    }, [categories])
    const updateCategorys = (categories) => {
        if (!categories || categories.length === 0) return []
        if (
            expoConfig.slug != 'shell-somosprotagonistas' &&
            expoConfig.slug != 'dots-ii'
        ) {
            return categories
        }
        let pendingCourses = []
        categories.forEach((category) => {
            const repo = Array.isArray(category.repository)
                ? category.repository
                : []
            let courses = repo.filter(
                (course) => course.status == LEARNING_STATUS.PENDIENTE,
            )

            if (courses.length > 0) {
                pendingCourses = [...pendingCourses, ...courses]
            }
        })
        let filteredCourses = []
        for (let i = 0; i < pendingCourses.length; i++) {
            const course = pendingCourses[i]
            if (!filteredCourses.some((c) => c._id === course._id)) {
                filteredCourses.push(course)
            }
        }
        let newCategory = {
            _id: '-1',
            category_name: 'Cursos pendientes',
            category_index: 1,
            blocked_by: [],
            repository: [],
        }
        newCategory.repository = filteredCourses
        let allCategories = [...categories]
        if (newCategory.repository.length > 0) {
            allCategories.unshift(newCategory)
        }
        allCategories.sort((a, b) => {
            return a.category_index - b.category_index
        })
        return allCategories
    }
    let updatedCats = updateCategorys(categories)

    return (
        <View style={styles.container}>
            {categories && categories.length > 0 && (
                <>
                    {categories.length > 1 ? (
                        <View style={{ flex: 1 }}>
                            {expoConfig.slug == 'branca-one' && (
                                <View
                                    style={{
                                        paddingHorizontal: 10,
                                        paddingTop: 10,
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 20,
                                            fontWeight: 'bold',
                                            color: theme.colors.textPrimary,
                                            marginBottom: 5,
                                        }}
                                    >
                                        {'Academia Branca'}
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 16,
                                            fontWeight: 'normal',
                                            color: theme.colors.textSecondary,
                                            paddingBottom: 10,
                                            borderBottomColor:
                                                theme.colors.lightGray,
                                            borderBottomWidth: 1,
                                            marginBottom: 10,
                                        }}
                                    >
                                        {
                                            'En esta sección vas a poder encontrar cursos para capacitarte y mejorar tu rendimiento.'
                                        }
                                    </Text>
                                </View>
                            )}

                            <FlashList
                                refreshControl={
                                    <RefreshControl
                                        refreshing={refresing}
                                        onRefresh={onRefresh}
                                        progressViewOffset={10}
                                    />
                                }
                                data={updatedCats}
                                keyExtractor={(item) => item._id}
                                horizontal={false}
                                showsVerticalScrollIndicator={false}
                                estimatedItemSize={20}
                                extraData={reload}
                                renderItem={({ item }) => (
                                    <CategoryItem
                                        data={item}
                                        categories={updatedCats}
                                        reload={reload}
                                        single={false}
                                        isMobile={isMobile}
                                    />
                                )}
                            />
                        </View>
                    ) : (
                        <CategoryItem
                            data={updatedCats[0]}
                            categories={updatedCats}
                            reload={reload}
                            single={true}
                            isMobile={isMobile}
                        />
                    )}
                </>
            )}

            {/* {(!categories || categories.length === 0) && (
                <View
                    style={{
                        top: '30%',
                        flex: 1,
                    }}
                >
                    <BottomSheetAlert
                        image={require('../../../assets/noCourses.png')}
                        title={'No hay cursos disponibles'}
                        desc={'Vuelve en otro momento'}
                    />
                </View>
            )} */}
            <LoadingOverlay visible={!!(isLoading || isFetching)} />
        </View>
    )
}

export default LearningHome
